// src/components/dataDisplay/AddCategoryForm.tsx

import { useState } from 'react';
import {
  Button,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons'; // For "remove row" button
import axios from 'axios';
import { useApi } from 'components/hooks/useApi';
import { useTenant } from 'components/hooks/useTenant';
import { useToken } from 'components/hooks/useToken';

interface CategoryInput {
  name: string;
  batch_category: boolean;
  expiry_category: boolean;
}

interface AddCategoryFormProps {
  /** Function to refetch or refresh the list of categories in the parent */
  refreshCategories: () => void;
}

export default function AddCategoryForm({ refreshCategories }: AddCategoryFormProps) {
  const apiUrl = useApi();
  const accessToken = useToken();
  const tenant = useTenant();
  const toast = useToast();

  // Modal controls
  const { isOpen, onOpen, onClose } = useDisclosure();

  // We’ll maintain an array of category objects in state.
  const [categories, setCategories] = useState<CategoryInput[]>([
    { name: '', batch_category: false, expiry_category: false },
  ]);

  /**
   * Add a new empty row for the user to fill in.
   */
  const addCategoryRow = () => {
    setCategories((prev) => [
      ...prev,
      { name: '', batch_category: false, expiry_category: false },
    ]);
  };

  /**
   * Remove a specific row by index.
   */
  const removeCategoryRow = (index: number) => {
    setCategories((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Update a specific field (name, batch_category, expiry_category) in a row.
   */
  const handleInputChange = (
    index: number,
    field: keyof CategoryInput,
    value: string | boolean
  ) => {
    setCategories((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddMultiple = async () => {
    try {
      // Basic validation: at least one row must have a name
      const validRows = categories.filter((cat) => cat.name.trim() !== '');
      if (validRows.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'Please provide at least one category name.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Prepare the payload to match your backend’s expected structure
      const payload = {
        Categories: validRows,
      };

      // POST the payload
      await axios.post(`${apiUrl}/categories`, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          tenant: tenant,
        },
      });

      toast({
        title: 'Categories Added',
        description: `Successfully added ${validRows.length} categories.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Clear state (or reset to a single blank row)
      setCategories([{ name: '', batch_category: false, expiry_category: false }]);
      // Close modal
      onClose();
      // Refresh parent’s category list
      refreshCategories();
    } catch (error: any) {
      console.error(error);
      toast({
        title: 'Error creating categories',
        description: error.message || 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleModalClose = () => {
    // If you want to reset everything on close, do so here:
    // setCategories([{ name: '', batch_category: false, expiry_category: false }]);
    onClose();
  };

  return (
    <>
      {/* The button that triggers the "Add Categories" modal */}
      <Button
        variant="brand"
        fontSize={{ base: 'xs', md: 'sm' }}
        w={{ base: 'auto', md: '150px' }}
        minW="0"
        h="auto"
        px={{ base: 2, md: 4 }}
        py={{ base: 1, md: 2 }}
        borderRadius="16px"
        whiteSpace="normal"
        textAlign="center"
        onClick={onOpen}
        aria-label="Add Category"
      >
        Add Categories
      </Button>

      <Modal isOpen={isOpen} onClose={handleModalClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add categories</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* We’ll map over `categories` to create a row of inputs per category */}
            {categories.map((cat, idx) => (
            <Flex key={idx} mb={4} alignItems="center">
            <FormControl display="flex" alignItems="center" mr={4}>
              {/* Put label next to input, remove default Chakra spacing */}
              <Input
                id={`category-${idx}`}
                placeholder="Category name"
                size="sm"
                value={cat.name}
                onChange={(e) => handleInputChange(idx, 'name', e.target.value)}
              />
            </FormControl>
          
            <Checkbox
              mr={4}
              isChecked={cat.batch_category}
              onChange={(e) => handleInputChange(idx, 'batch_category', e.target.checked)}
            >
              Batched?
            </Checkbox>
          
            <Checkbox
              mr={4}
              isChecked={cat.expiry_category}
              onChange={(e) => handleInputChange(idx, 'expiry_category', e.target.checked)}
            >
              Expires?
            </Checkbox>
          
            <IconButton
              aria-label="Remove category row"
              icon={<DeleteIcon />}
              size="sm"
              variant="brand"
              onClick={() => removeCategoryRow(idx)}
              isDisabled={categories.length === 1}
            />
          </Flex>
            ))}
            {/* Add another empty row */}
            <Button size="sm" variant="brand" onClick={addCategoryRow}>
              + Add Another Category
            </Button>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>
              Cancel
            </Button>
            <Button variant="brand" onClick={handleAddMultiple}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
